import * as React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {
    ApplicationContextConsumer,
    bindLoadingHandler,
    LoadingOverlayHandler,
    withLoadingOverlay
} from "../app/ApplicationContext";
import {ErrorAlert} from "../common/ErrorAlert";
import {
    ChecklistAlert,
    ChecklistState,
    ErrorResponse,
    GridRequest,
    GridRequestFilters,
    PageResponseEnvelope,
} from "../api/dtos/dtos";
import * as tableUtils from "../utils/table-utils";
import {
    filterableColumn,
    GridState,
    noTitleColumn,
    searchableColumn,
    simpleColumn,
    sortableColumn
} from "../utils/table-utils";
import locales from "../locales";
import {Button, Table} from "antd";
import ButtonBar from "../common/ButtonBar";
import furoApi from "../api/FuroApi";
import {ColumnProps} from "antd/lib/table";
import ButtonGroup from "antd/lib/button/button-group";
import {formatDateTime, toApiDateFormat} from "../utils/date-utils";
import {ChecklistRentTypeFilter} from "./ChecklistRentTypeFilter";
import ChecklistFilterSelector from "./ChecklistFilterSelector";
import moment from "moment";
import * as paths from "../paths";
import {DateRangePicker} from "../common/DateRangePicker";
import {Link} from "react-router-dom";
import {getChecklistAlertStateFilters} from "../checklist/dtos";
import {cacheComponentStateForLocation} from "../utils/router-utils";
import ChecklistStateLabel from "../checklist/ChecklistStateLabel";
import {renderItemName} from "../item/item-name-renderer";
import IdStringLabel from "../rental/IdStringLabel";


interface State extends GridState<ChecklistAlert> {
    error?: ErrorResponse;
    rentTypeFilter: ChecklistRentTypeFilter;
    startFilter?: moment.Moment;
    endFilter?: moment.Moment;
}

interface ChecklistAlertsPageLocationState {
    start?: string;
    end?: string;
    rentType: ChecklistRentTypeFilter;
    sortOrder?: 'ascend' | 'descend';
    filters?: GridRequestFilters;
}

const FIELD_ITEM_NAME = "itemName"
const FIELD_ITEM_INVENTORY = "inventoryNumber"
const FIELD_CREATED = "created"
const FIELD_CHECKLIST_STATE = "checklistState"

const initialGridState = (): GridState<ChecklistAlert> => {
    return {
        ...tableUtils.initialGridState(),
        filters: {
            [FIELD_CHECKLIST_STATE]: [ChecklistState.MISSING, ChecklistState.MISSING_ATTACHMENTS]
        }
    }
};

class ChecklistAlertsPage extends React.Component<RouteComponentProps<{}, {}, ChecklistAlertsPageLocationState>, State> {

    public readonly loadingHandler?: LoadingOverlayHandler;

    constructor(props: RouteComponentProps<{}, {}, ChecklistAlertsPageLocationState>) {
        super(props);

        const locationState = this.props.location.state;

        const type = locationState?.rentType ?? ChecklistRentTypeFilter.RENTAL
        this.state = {
            ...initialGridState(),

            startFilter: locationState?.start ? moment(locationState.start) : undefined,
            endFilter: locationState?.end ? moment(locationState?.end) : undefined,
            rentTypeFilter: type,
            sortOrder: type === ChecklistRentTypeFilter.RENTAL ? 'descend' : 'ascend',
            sortColumnKey: FIELD_CREATED,
            filters: (locationState?.filters?.[FIELD_CHECKLIST_STATE]?.length ?? 0) > 0 ? locationState?.filters : {
                [FIELD_CHECKLIST_STATE]: [ChecklistState.MISSING, ChecklistState.MISSING_ATTACHMENTS]
            },

        }
    }

    public componentDidMount() {
        this.reload();
    }

    public render(): React.ReactNode {
        const {error, rentTypeFilter, startFilter, endFilter} = this.state;

        const columns: Array<ColumnProps<ChecklistAlert>> = [];

        if (rentTypeFilter === ChecklistRentTypeFilter.RENTAL) {
            columns.push(noTitleColumn("rental.actions", this.renderRentalActionCell, 56));
        } else {
            columns.push(noTitleColumn("returnal.actions", this.renderReturnalActionCell, 56));
        }

        columns.push(...[
            {
                ...filterableColumn(locales.state, FIELD_CHECKLIST_STATE, this.state, (r: ChecklistAlert) => this.renderChecklistAlertStateCondensed(r), getChecklistAlertStateFilters(), true),
                width: 132,
            },
            searchableColumn(locales.item, FIELD_ITEM_NAME, this.state, this.renderItem),
            searchableColumn(locales.inventoryNumber, FIELD_ITEM_INVENTORY, this.state, (r: ChecklistAlert) => r.rentalItem.item.inventoryNumber),
        ]);

        if (rentTypeFilter === ChecklistRentTypeFilter.RENTAL) {
            columns.push(
                simpleColumn(locales.rental, this.renderRentalId),
                sortableColumn(locales.rental_date, FIELD_CREATED, this.state, (r: ChecklistAlert) => r.rental ? formatDateTime(r.rental?.date) : undefined),
            )
        } else {
            columns.push(
                simpleColumn(locales.return, this.renderReturnalId),
                sortableColumn(locales.return_date, FIELD_CREATED, this.state, (r: ChecklistAlert) => r.returnal ? formatDateTime(r.returnal?.date) : undefined),
            );
        }

        return (
            <>
                <ApplicationContextConsumer>
                    {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
                </ApplicationContextConsumer>

                <ButtonBar style={{marginBottom: 8}}>
                    <ChecklistFilterSelector
                        value={this.state.rentTypeFilter}
                        onChange={this.onChecklistFilterChange}
                    />
                    <DateRangePicker
                        onRangeChanged={this.onDateRangeFilterChange}
                        defaultStart={startFilter}
                        defaultEnd={endFilter}
                    />
                </ButtonBar>

                <Table
                    dataSource={this.state.data}
                    columns={columns}
                    bordered={true}
                    size="small"
                    rowKey={this.getRowKey}
                    pagination={tableUtils.defaultPaginationConfig(this.state)}
                    onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
                    scroll={{x: 1024}}
                />

                {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
            </>
        );
    }

    private getRowKey = (record: ChecklistAlert): string => {
        return tableUtils.idRowKeyFn(record.rentalItem)
    }

    private onGridChange = (newState: GridState<ChecklistAlert>) => {
        this.setState({...newState}, () => this.reload());
    };

    private onChecklistFilterChange = (filter: ChecklistRentTypeFilter) => {
        this.setState({
                rentTypeFilter: filter,
                sortOrder: filter === ChecklistRentTypeFilter.RENTAL ? 'descend' : 'ascend',
            },
            () => this.reload());
    };

    private onDateRangeFilterChange = (start?: moment.Moment, end?: moment.Moment) => {
        this.setState({startFilter: start, endFilter: end}, () => this.reload());

    };

    private renderItem = (record: ChecklistAlert) => {
        const item = record.rentalItem.item;
        return <Link to={paths.viewItemPath(item.id)}>{renderItemName(item)}</Link>;
    };

    private renderRentalId = (record: ChecklistAlert): React.ReactNode => {
        const {rental} = record;
        if (rental) {
            const onViewClick = () => this.openRentalDetails(rental.id)
            return (
                <IdStringLabel
                    idString={rental.idString}
                    type="rental"
                    onClick={onViewClick}
                />
            );
        }
        return null;
    };

    private openRentalDetails = (rentalID: number) => {
        this.props.history.push(paths.viewRentalPath(rentalID));
    };

    private renderReturnalId = (record: ChecklistAlert) => {
        const {returnal} = record;
        if (returnal) {
            const onViewClick = () => this.openReturnalDetails(returnal.id)
            return (
                <IdStringLabel
                    idString={returnal.idString}
                    type="returnal"
                    onClick={onViewClick}
                />
            );
        }
        return null;
    };

    private openReturnalDetails = (returnalID: number) => {
        this.props.history.push(paths.viewReturnalPath(returnalID));
    };

    private renderRentalActionCell = (record: ChecklistAlert) => {
        const onCreateClick = () => this.onCreateRentalChecklistClick(record);
        const onEditClick = () => this.onEditRentalChecklistClick(record);
        return (
            <div className="action-column" style={{textAlign: 'center', whiteSpace: 'nowrap'}}>
                <ButtonGroup>
                    {!record.rentalChecklist?.id &&
                        <Button icon="plus" onClick={onCreateClick} title={locales.add}/>}
                    {record.rentalChecklist?.id &&
                        <Button icon="edit" onClick={onEditClick} title={locales.edit}/>}
                </ButtonGroup>
            </div>
        );
    };

    private renderReturnalActionCell = (record: ChecklistAlert) => {
        const onEditClick = () => this.onEditReturnalChecklistClick(record);
        const onCreateClick = () => this.onCreateReturnalChecklistClick(record);
        return (
            <div className="action-column" style={{textAlign: 'center', whiteSpace: 'nowrap'}}>
                <ButtonGroup>
                    {!record.returnalChecklist?.id && record.returnal &&
                        <Button icon="plus" onClick={onCreateClick} title={locales.add}/>}
                    {record.returnalChecklist?.id &&
                        <Button icon="edit" onClick={onEditClick} title={locales.edit}/>}
                </ButtonGroup>
            </div>
        );
    };

    private renderChecklistAlertStateCondensed(r: ChecklistAlert): React.ReactNode {
        const state = r.returnalChecklist?.state ?? r.rentalChecklist!.state;
        return <ChecklistStateLabel state={state}/>
    }

    private onCreateRentalChecklistClick = (alert: ChecklistAlert) => {
        this.props.history.push(paths.createChecklistPath({
            rentalItemId: alert.rentalItem.id!,
            rentalId: alert.rental!.id!
        }))
    };

    private onEditRentalChecklistClick = (alert: ChecklistAlert) => {
        this.props.history.push(paths.viewChecklistPath(alert.rentalChecklist?.id));
    };

    private onCreateReturnalChecklistClick = (alert: ChecklistAlert) => {
        this.props.history.push(paths.createChecklistPath({
            rentalItemId: alert.rentalItem.id!,
            returnalId: alert.returnal?.id!
        }))
    };

    private onEditReturnalChecklistClick = (alert: ChecklistAlert) => {
        this.props.history.push(paths.viewChecklistPath(alert.returnalChecklist?.id));
    };

    private reload = () => {
        const request = tableUtils.toGridRequest(this.state);
        this.addExtraFiltersToGridRequest(request);

        let api = furoApi.checklistAlerts.getRentalChecklistAlerts;
        if (this.state.rentTypeFilter === ChecklistRentTypeFilter.RETURNAL) {
            api = furoApi.checklistAlerts.getReturnalChecklistAlerts;
        }

        const promise = api(request)
            .then(this.onReloadSuccess)
            .catch(this.onApiError);
        withLoadingOverlay(promise, this.loadingHandler);
    };

    private onReloadSuccess = (response: PageResponseEnvelope<ChecklistAlert>) => {
        cacheComponentStateForLocation(this.props, {
            rentType: this.state.rentTypeFilter,
            start: this.state.startFilter?.toJSON(),
            end: this.state.endFilter?.toJSON(),
            sortOrder: this.state.sortOrder,
            filters: this.state.filters,
        });

        this.setState({
            ...tableUtils.toGridState(response),
            error: undefined,
        });


    };

    private onApiError = (error: ErrorResponse) => {
        this.setState({error})
    };

    private addExtraFiltersToGridRequest(request: GridRequest) {
        const start = this.state.startFilter;
        const end = this.state.endFilter;

        if (start !== null) {
            request.filters = {
                ...request.filters,
                'start': start ? [toApiDateFormat(start)] : [],
            };
        }
        if (end !== null) {
            request.filters = {
                ...request.filters,
                'end': end ? [toApiDateFormat(end)] : [],
            };
        }
    }

}

export default withRouter(ChecklistAlertsPage);



// WEBPACK FOOTER //
// ./src/checklist-alerts/ChecklistAlertsPage.tsx